/**
 * Shared Theme Utility
 * Provides consistent theme management across all MFEs
 */

export type ThemeMode = 'light' | 'dark' | 'editorial' | 'meadow' | 'royal' | 'sunset' | 'ocean';

/** Order the header's cycle button walks. Also the order shown in the picker. */
export const THEME_MODES: ThemeMode[] = ['light', 'dark', 'editorial', 'meadow', 'royal', 'sunset', 'ocean'];

/* Every theme other than Light rides on the `.dark` class, because Tailwind is
   configured darkMode:'class' and ~1,800 `dark:` utilities across the app key
   off it. `data-theme` is what actually distinguishes them, so a palette is
   selected with `.dark[data-theme='…']` — one specificity step above the base
   `.dark` rules, which is what lets a theme override them. */
const DARK_CLASS_MODES: ThemeMode[] = ['dark', 'editorial', 'meadow', 'royal', 'sunset', 'ocean'];

const THEME_STORAGE_KEY = 'themeMode';

/**
 * Get the current theme from localStorage
 */
export const getTheme = (): ThemeMode => {
  if (typeof window === 'undefined') return 'light';

  const savedTheme = localStorage.getItem(THEME_STORAGE_KEY) as ThemeMode | null;
  // A value written by an older build (or a hand-edited one) must not leave the
  // app on a theme that has no stylesheet — fall back rather than half-apply.
  return savedTheme && THEME_MODES.includes(savedTheme) ? savedTheme : 'light';
};

/**
 * Set the theme and apply it to the document
 */
export const setTheme = (mode: ThemeMode): void => {
  if (typeof window === 'undefined') return;

  const previous = getTheme();

  // Save to localStorage
  localStorage.setItem(THEME_STORAGE_KEY, mode);

  // Apply to document
  applyThemeToDocument(mode);

  // Dispatch storage event to notify other windows/tabs and MFEs
  window.dispatchEvent(
    new StorageEvent('storage', {
      key: THEME_STORAGE_KEY,
      newValue: mode,
      oldValue: previous,
    })
  );
};

/**
 * Apply theme to document element
 */
export const applyThemeToDocument = (mode: ThemeMode): void => {
  if (typeof window === 'undefined') return;

  const root = document.documentElement;

  // Set data-theme attribute — this is what selects the palette
  root.setAttribute('data-theme', mode);

  // Toggle dark class for Tailwind (shared by every non-light theme)
  if (DARK_CLASS_MODES.includes(mode)) {
    root.classList.add('dark');
  } else {
    root.classList.remove('dark');
  }
};

/**
 * Switch to a specific theme
 */
export const setThemeMode = (mode: ThemeMode): ThemeMode => {
  setTheme(mode);
  return mode;
};

/**
 * Step to the next theme in THEME_MODES, wrapping at the end.
 *
 * Kept named `toggleTheme` because the header's icon button and several call
 * sites already use it; with three themes it is a cycle rather than a flip.
 */
export const toggleTheme = (): ThemeMode => {
  const index = THEME_MODES.indexOf(getTheme());
  const newTheme = THEME_MODES[(index + 1) % THEME_MODES.length];
  setTheme(newTheme);
  return newTheme;
};

/**
 * Initialize theme on app startup
 */
export const initializeTheme = (): ThemeMode => {
  const theme = getTheme();
  applyThemeToDocument(theme);
  return theme;
};

/**
 * Listen for theme changes from other sources (main_fe or other tabs)
 * Returns a cleanup function to remove the listener
 */
export const subscribeToThemeChanges = (
  callback: (theme: ThemeMode) => void
): (() => void) => {
  if (typeof window === 'undefined') return () => {};

  const handleStorageChange = (e: StorageEvent) => {
    if (e.key === THEME_STORAGE_KEY && e.newValue) {
      const newTheme = e.newValue as ThemeMode;
      if (!THEME_MODES.includes(newTheme)) return;
      applyThemeToDocument(newTheme);
      callback(newTheme);
    }
  };

  window.addEventListener('storage', handleStorageChange);

  // Return cleanup function
  return () => {
    window.removeEventListener('storage', handleStorageChange);
  };
};
